import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { db } from '../db';
import { supabase } from '../lib/supabase';
import type { Settings } from '../types';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const [settings, setSettings] = useState<Settings>({
    id: 'default',
    autoSaveInterval: 5 * 60 * 1000,
    skuBatchSize: 20,
    showNotifications: true,
    deleteLogsAfterDays: 30,
    adminPassword: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadSettings();
    }
  }, [isOpen]);

  const loadSettings = async () => {
    try {
      const savedSettings = await db.settings.get('default');
      if (savedSettings) {
        setSettings(savedSettings);
      }
    } catch (error: any) {
      console.error('설정 로드 실패:', error.message);
      toast.error(`설정을 불러오는데 실패했습니다: ${error.message}`);
    }
  };

  const saveSettings = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      // 유효성 검사
      if (settings.autoSaveInterval < 60 * 1000) {
        throw new Error('자동 저장 주기는 최소 1분 이상이어야 합니다.');
      }
      if (settings.skuBatchSize < 1) {
        throw new Error('SKU 배치 크기는 1 이상이어야 합니다.');
      }
      if (settings.deleteLogsAfterDays < 1) {
        throw new Error('로그 보관 기간은 1일 이상이어야 합니다.');
      }

      // Supabase 동기화
      if (navigator.onLine) {
        const { error } = await supabase
          .from('settings')
          .upsert({
            id: settings.id,
            auto_save_interval: settings.autoSaveInterval,
            sku_batch_size: settings.skuBatchSize,
            show_notifications: settings.showNotifications,
            delete_logs_after_days: settings.deleteLogsAfterDays,
            admin_password: settings.adminPassword,
            user_id: (await supabase.auth.getSession()).data.session?.user.id,
          });

        if (error) throw new Error(`Supabase 동기화 실패: ${error.message}`);
      } else {
        toast.warn('오프라인 상태: 설정이 로컬에만 저장되었습니다.');
      }

      // 로컬 DB 저장
      await db.settings.put(settings);

      // 로그 추가
      await db.logs.add({
        id: crypto.randomUUID(),
        timestamp: new Date(),
        type: 'settings',
        message: '설정이 업데이트되었습니다',
      });

      // 오래된 로그 삭제
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - settings.deleteLogsAfterDays);
      await db.logs
        .where('timestamp')
        .below(cutoffDate)
        .delete();

      toast.success('설정이 저장되었습니다');
      onClose();
    } catch (error: any) {
      console.error('설정 저장 실패:', error.message);
      toast.error(`설정 저장에 실패했습니다: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">설정</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
            disabled={isSaving}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              자동 저장 주기 (분)
            </label>
            <input
              type="number"
              min="1"
              value={settings.autoSaveInterval / (60 * 1000)}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value >= 1) {
                  setSettings({
                    ...settings,
                    autoSaveInterval: value * 60 * 1000,
                  });
                }
              }}
              className="w-full px-3 py-2 border rounded-md"
              disabled={isSaving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SKU 배치 크기
            </label>
            <input
              type="number"
              min="1"
              value={settings.skuBatchSize}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value >= 1) {
                  setSettings({
                    ...settings,
                    skuBatchSize: value,
                  });
                }
              }}
              className="w-full px-3 py-2 border rounded-md"
              disabled={isSaving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              로그 보관 기간 (일)
            </label>
            <input
              type="number"
              min="1"
              value={settings.deleteLogsAfterDays}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (value >= 1) {
                  setSettings({
                    ...settings,
                    deleteLogsAfterDays: value,
                  });
                }
              }}
              className="w-full px-3 py-2 border rounded-md"
              disabled={isSaving}
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="showNotifications"
              checked={settings.showNotifications}
              onChange={(e) => setSettings({
                ...settings,
                showNotifications: e.target.checked,
              })}
              className="h-4 w-4 text-blue-600 rounded border-gray-300"
              disabled={isSaving}
            />
            <label
              htmlFor="showNotifications"
              className="ml-2 text-sm text-gray-700"
            >
              알림 표시
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              관리자 비밀번호
            </label>
            <input
              type="password"
              value={settings.adminPassword}
              onChange={(e) => setSettings({
                ...settings,
                adminPassword: e.target.value,
              })}
              className="w-full px-3 py-2 border rounded-md"
              disabled={isSaving}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            disabled={isSaving}
          >
            취소
          </button>
          <button
            onClick={saveSettings}
            className={`px-4 py-2 rounded-md transition-colors ${
              isSaving
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
            disabled={isSaving}
          >
            {isSaving ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                저장 중
              </div>
            ) : (
              '저장'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}